import type { FC, PropsWithChildren } from 'react';
import type { SystemStyleObject } from '@chakra-ui/react';
import { Accordion } from './Accordion';

interface AccordionOverlapProps extends PropsWithChildren {
    id?: string;
    defaultIndex?: number | number[];
    onChange?: (index: number | number[]) => void;
    index?: number | number[];
    allowMultiple?: boolean;
    className?: string;
}

export const AccordionOverlap: FC<AccordionOverlapProps> = ({
    id,
    children,
    defaultIndex = 0,
    onChange,
    index,
    allowMultiple = true,
    className,
}) => {
    const resolvedDefaultIndex: number | number[] =
        allowMultiple && !Array.isArray(defaultIndex) ? [defaultIndex] : defaultIndex;

    const overlapStyles: SystemStyleObject = {
        // Add bottom padding to panels when open, or buttons when closed (for non-last items)
        '& > *:not(:last-of-type) div[data-testid$="-content"]': {
            paddingBottom: 8,
        },
        // Add bottom padding to buttons when closed (for non-last items)
        '& > *:not(:last-of-type) > button[data-testid*="toggle-button"]:not([aria-expanded="true"])':
            {
                paddingBottom: 8,
            },
        // Add negative margin-top to buttons in non-first items
        '& > *:not(:first-of-type) button[data-testid*="toggle-button"]': {
            marginTop: -4,
        },
        // Add border to last button when closed
        '& > *:last-of-type button[data-testid*="toggle-button"]:not([aria-expanded="true"])': {
            borderWidth: '1px',
            borderColor: 'slt-pepper.300',
            borderStyle: 'solid',
            paddingBottom: 4,
        },
    };

    return (
        <div className={className}>
            <Accordion
                id={id}
                variant="overlapping"
                defaultIndex={resolvedDefaultIndex}
                onChange={onChange}
                index={index}
                allowMultiple={allowMultiple}
                sx={overlapStyles}
            >
                {children}
            </Accordion>
        </div>
    );
};
